<script setup lang="ts">
import { computed } from 'vue'
import PButton from 'primevue/button'
import PTag from 'primevue/tag'
import CBaseCard from '@/presentation/components/base/CBaseCard.vue'

export interface DiagnosticCardData {
  id: string
  title: string
  type: 'diagnostic'
  status: 'pending' | 'active' | 'completed' | 'disabled'
  courseName?: string
  description?: string
}

export interface ModuleCardData {
  id: number
  title: string
  type: 'module'
  status: 'pending' | 'active' | 'completed' | 'disabled'
  courseName: string
  visible: boolean
}

type CardData = DiagnosticCardData | ModuleCardData

// PROPS
const props = defineProps<{
  card: CardData
  loading?: boolean
}>()

// EMITS
const emit = defineEmits<{
  'activate-diagnostic': [diagnosticId: string]
  'activate-module': [moduleId: number]
  'view-results': [cardId: string | number]
  'view-content': [cardId: string | number]
}>()

// COMPUTED
const cardTitle = computed(() => props.card.title)

const cardStatus = computed(() => props.card.status)

const isDisabled = computed(() => cardStatus.value === 'disabled')

const isActive = computed(() => cardStatus.value === 'active')

const isCompleted = computed(() => cardStatus.value === 'completed')

const isPending = computed(() => cardStatus.value === 'pending')

const statusSeverity = computed(() => {
  switch (cardStatus.value) {
    case 'completed':
      return 'success'
    case 'active':
      return 'info'
    case 'pending':
      return 'warning'
    case 'disabled':
    default:
      return 'secondary'
  }
})

const statusLabel = computed(() => {
  if (props.card.type === 'diagnostic') {
    switch (cardStatus.value) {
      case 'completed':
        return 'Diagnostic publié'
      case 'pending':
        return 'Prêt à être publié'
      case 'disabled':
        if (props.card.id === 'D1') {
          return 'Indisponible (aucun module assigné)'
        }
        return 'Indisponible'
      default:
        return 'Indisponible'
    }
  } else {
    switch (cardStatus.value) {
      case 'completed':
        return 'Module publié'
      case 'pending':
        return 'Prêt à être publié'
      case 'disabled':
      default:
        return 'Indisponible (diagnostic D1 requis)'
    }
  }
})

const courseTag = computed(() => {
  if (props.card.type === 'diagnostic') return 'DIAGNOSTIC'
  return 'MODULE'
})

// METHODS
function handlePrimaryAction() {
  if (props.card.type === 'diagnostic') {
    if (isPending.value) {
      emit('activate-diagnostic', props.card.id)
    }
  } else {
    if (isPending.value) {
      emit('activate-module', props.card.id)
    }
  }
}

function handleViewResults() {
  console.log('handleViewResults', props.card.id)
  emit('view-results', props.card.id)
}

function handleViewContent() {
  emit('view-content', props.card.id)
}

function getPrimaryActionLabel() {
  if (props.card.type === 'diagnostic') {
    if (isCompleted.value) return 'Voir les résultats'
    return `Publier le diagnostic ${props.card.id}`
  } else {
    if (isCompleted.value) return 'Voir les résultats'
    return 'Activer le module'
  }
}
</script>

<template>
  <c-base-card
    has-footer
    :class="{
      'opacity-50': isDisabled,
      'ring-2 ring-blue-200': isActive,
      'ring-2 ring-green-200': isCompleted
    }"
  >
    <template #header>
      <div class="flex items-start justify-between">
        <p-tag :value="courseTag" :severity="statusSeverity" class="mb-2 text-xs uppercase" />
      </div>
    </template>

    <template #content>
      <div class="space-y-3">
        <h4 class="text-base font-medium">{{ cardTitle }}</h4>

        <div class="text-sm">
          <p v-if="card.courseName && card.type === 'module'" class="mb-1 text-gray-600">
            Parcours : {{ card.courseName }}
          </p>

          <p v-if="'description' in card && card.description" class="mb-2 text-gray-600">
            {{ card.description }}
          </p>

          <div class="mt-2 flex items-center gap-2">
            <span
              :class="
                statusSeverity === 'success'
                  ? 'text-green-600'
                  : statusSeverity === 'info'
                    ? 'text-blue-600'
                    : statusSeverity === 'warning'
                      ? 'text-orange-600'
                      : 'text-gray-600'
              "
            >
              {{ statusLabel }}
            </span>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="mt-4 flex flex-col items-center justify-center gap-2">
        <p-button
          :label="getPrimaryActionLabel()"
          severity="primary"
          icon="pi pi-chart-bar"
          :outlined="isCompleted"
          size="small"
          @click="isCompleted ? handleViewResults() : handlePrimaryAction()"
          :loading="loading"
          :disabled="isDisabled"
        />

        <p-button
          label="Voir les contenus"
          icon="pi pi-external-link"
          icon-pos="right"
          text
          severity="secondary"
          size="small"
          @click="handleViewContent()"
          :disabled="isDisabled"
        />
      </div>
    </template>
  </c-base-card>
</template>
